import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Optional,
    Renderer2,
    Self,
    HostBinding
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { CalendarArray, DateSelectionEvent } from "@spscommerce/ds-shared";
import * as momentImport from "moment-timezone";
import { Moment } from "moment-timezone";

import { DATE_FORMAT } from "../../../tokens/index";
import {
    minDateValidator,
    maxDateValidator,
} from "../../../validators/index";
import { SpsAbstractDatepicker } from "../sps-abstract-datepicker";

const moment = momentImport as any; // required for rollup to work without a TypeScript error

@Component({
    selector: "sps-datepicker",
    templateUrl: "./sps-datepicker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsDatepickerComponent extends SpsAbstractDatepicker<Moment> {
    static readonly displayName = "sps-datepicker";
    static readonly props = {
        ...SpsAbstractDatepicker.props,
    };

    weeks: CalendarArray;

    @HostBinding("class.sps-datepicker") readonly spsDatepickerClass = true;

    constructor(
        _changeDetector: ChangeDetectorRef,
        @Inject(DATE_FORMAT) _dateFormat: string,
        _el: ElementRef,
        _renderer: Renderer2,
        @Self()
        @Optional()
        _ngControl: NgControl
    ) {
        super(_changeDetector, _dateFormat, _el, _renderer, _ngControl);
        this.placeholder = this.formatString;
    }

    parse(value: string): Moment {
        return moment(`${value} 00:00:00`);
    }

    updateText() {
        this.text = this.value ? this.value.format(this._dateFormat) : "";
    }

    handleDateSelected(event: DateSelectionEvent<Moment>) {
        this.value = event.value;
        this.updateText();
        delete this.keyboardFocusDay;
        this.inputElement.nativeElement.focus();
        this.close();
    }

    setView(displayMoment?: Moment) {
        if (this.value && this._ngControl && this._ngControl.control) {
            this._ngControl.control.setErrors(
                minDateValidator(this._minDateMoment)(this._ngControl.control)
                || maxDateValidator(this._maxDateMoment)(this._ngControl.control)
            );
        }

        if (!displayMoment || !displayMoment.isValid()) {
            if (this.value && this.value.isValid()) {
                displayMoment = this.value.clone();
            } else if (this.minDate) {
                displayMoment = this._minDateMoment.clone();
            } else if (this.maxDate) {
                displayMoment = this._maxDateMoment.clone();
            } else {
                displayMoment = moment();
            }
        }
        this.view = displayMoment.set({
            date: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0
        });
        this._changeDetector.markForCheck();
    }

    focusCalendar() {
        super.focusCalendar();
        this.keyboardFocusDay = this.view.clone();
    }

    viewPreviousMonth() {
        if (!this.minDate || this.view.isAfter(this._minDateMoment, "month")) {
            this.setView(this.view.subtract(1, "month"));
        }
    }

    viewNextMonth() {
        if (!this.maxDate || this.view.isBefore(this._maxDateMoment, "month")) {
            this.setView(this.view.add(1, "month"));
        }
    }

    @HostListener("keydown", ["$event"])
    onKeyDown(event: KeyboardEvent) {
        super.onKeyDown(event);
        this.calendar.onKeyDown(event);
    }

    clear() {
        this.value = undefined;
        this.updateText();
        this.inputElement.nativeElement.focus();
    }
}
